import { useState, useEffect } from 'react';
import logoIcon from '../../assets/images/standalone_logo.png';
import logoFull from '../../assets/images/logo_aktivity.png';
import { formatActualDateTime } from '../../utils/dateUtils';

/**
 * Action Plans Activity Component
 * Includes action plan text, "By When" timeline, and commitment level
 * for calculating Action Readiness Score (ARS)
 * Supports multiple action plans per submission
 */

// Timeline options for "By When" dropdown
const BY_WHEN_OPTIONS = [
  { value: 'not_sure', label: 'Not sure yet', description: 'I need more time to decide' },
  { value: '7_days', label: 'Within 7 days', description: 'Immediate action' },
  { value: '15_days', label: 'Within 15 days', description: 'Very near-term' },
  { value: '30_days', label: 'Within 30 days', description: 'Near-term' },
  { value: '60_days', label: 'Within 60 days', description: 'Medium-term' },
  { value: '90_days', label: 'Within 90 days', description: 'Long-term' },
];

// Commitment level options (1-10)
const COMMITMENT_LEVELS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// Maximum number of action plans allowed
const MAX_ACTION_PLANS = 5;

// Empty action plan template
const createEmptyActionPlan = () => ({
  text: '',
  byWhen: '',
  commitment: 0,
});

const ActionPlansActivity = ({
  activity,
  sessionId,
  sessionCode,
  sessionName = 'Live Session',
  trainerName = 'Trainer',
  timeRemaining,
  onSubmit,
  onBackToList,
  showResults = false,
  userActionPlan = null,
  isActivityEnded = false,
}) => {
  // Draft key — scoped to (sessionId, activityId) so different activities /
  // sessions never bleed into each other. Null if either is missing.
  const activityKeyId = activity?._id || activity?.id;
  const draftKey = sessionId && activityKeyId
    ? `pending_actionplans_${sessionId}_${activityKeyId}`
    : null;

  // Support both single action plan (legacy) and multiple action plans
  const initializeActionPlans = () => {
    if (userActionPlan) {
      // Check if it's an array of action plans
      if (Array.isArray(userActionPlan.actionPlans) && userActionPlan.actionPlans.length > 0) {
        return userActionPlan.actionPlans.map(ap => ({
          text: ap.text || ap.action || '',
          byWhen: ap.byWhen || '',
          commitment: ap.commitment || 0,
        }));
      }
      // Legacy single action plan format
      const text = userActionPlan.text || userActionPlan.plan || userActionPlan.action || userActionPlan.actionPlan || '';
      if (text) {
        return [{
          text,
          byWhen: userActionPlan.byWhen || '',
          commitment: userActionPlan.commitment || 0,
        }];
      }
    }
    // Restore any in-progress draft (only if the user hasn't already
    // submitted) — handles the mobile thumb-zone collision with the
    // bottom nav that unmounts this component mid-typing.
    if (draftKey && !userActionPlan) {
      try {
        const saved = sessionStorage.getItem(draftKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        }
      } catch (_) { /* malformed / quota / private mode — ignore */ }
    }
    return [createEmptyActionPlan()];
  };

  const [actionPlans, setActionPlans] = useState(initializeActionPlans);
  const [activeIndex, setActiveIndex] = useState(0);
  const [submitError, setSubmitError] = useState('');

  // Update state when userActionPlan prop changes
  useEffect(() => {
    if (userActionPlan) {
      setActionPlans(initializeActionPlans());
    }
  }, [userActionPlan]);

  // Persist in-progress draft on every change (skipped once submitted or
  // the activity has ended — those states are read-only from the user POV).
  useEffect(() => {
    if (!draftKey) return;
    if (showResults || isActivityEnded) return;
    try {
      sessionStorage.setItem(draftKey, JSON.stringify(actionPlans));
    } catch (_) { /* silently ignore quota errors */ }
  }, [actionPlans, draftKey, showResults, isActivityEnded]);

  // Format time as M:SS
  const formatTime = (seconds) => {
    if (seconds === undefined || seconds === null || isNaN(seconds)) {
      return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate timer progress. Only render a timer when the trainer explicitly opted in
  // (`timerEnabled === true`). Default is disabled, so any activity missing this flag
  // (legacy data) is treated as untimed regardless of lingering duration/timeLimit.
  const maxTime = activity?.timerEnabled === true
    ? (activity?.timeLimit || (activity?.duration ? activity.duration * 60 : null) || activity?.config?.timeLimit || 0)
    : 0;
  const timerEnabled = maxTime > 0;

  // Get current action plan
  const currentPlan = actionPlans[activeIndex] || createEmptyActionPlan();

  // Check if current action plan is complete
  const isCurrentPlanComplete = currentPlan.text.trim() && currentPlan.byWhen && currentPlan.commitment > 0;

  // Check if at least one action plan is complete
  const hasCompletePlan = actionPlans.some(ap => ap.text.trim() && ap.byWhen && ap.commitment > 0);

  // Get label for byWhen value
  const getByWhenLabel = (value) => BY_WHEN_OPTIONS.find((o) => o.value === value)?.label || value;

  // Get commitment level description
  const getCommitmentDescription = (level) => {
    if (level <= 2) return 'Very Low';
    if (level <= 4) return 'Low';
    if (level <= 6) return 'Medium';
    if (level <= 8) return 'High';
    return 'Very High';
  };

  // Update a specific field in the current action plan
  const updateCurrentPlan = (field, value) => {
    setActionPlans(prev => prev.map((plan, idx) =>
      idx === activeIndex ? { ...plan, [field]: value } : plan
    ));
  };

  // Add a new action plan
  const addActionPlan = () => {
    if (actionPlans.length < MAX_ACTION_PLANS) {
      setActionPlans(prev => [...prev, createEmptyActionPlan()]);
      setActiveIndex(actionPlans.length);
    }
  };

  // Remove an action plan
  const removeActionPlan = (index) => {
    if (actionPlans.length > 1) {
      setActionPlans(prev => prev.filter((_, idx) => idx !== index));
      if (activeIndex >= index && activeIndex > 0) {
        setActiveIndex(activeIndex - 1);
      }
    }
  };

  const handleSubmit = () => {
    setSubmitError('');

    // Classify each plan: complete | partial | empty.
    // Empty plans (user added but never touched) are silently dropped.
    // Partial plans (user filled some fields but missed others) used to be
    // dropped silently — now we surface a clear error so the user doesn't
    // think a plan submitted when it didn't.
    const isComplete = (ap) => ap.text.trim() && ap.byWhen && ap.commitment > 0;
    const isEmpty = (ap) => !ap.text.trim() && !ap.byWhen && !ap.commitment;
    const isPartial = (ap) => !isComplete(ap) && !isEmpty(ap);

    const partialIndexes = actionPlans
      .map((ap, idx) => (isPartial(ap) ? idx : -1))
      .filter((idx) => idx >= 0);

    if (partialIndexes.length > 0) {
      const planLabels = partialIndexes.map((i) => `#${i + 1}`).join(', ');
      setSubmitError(
        `Action plan ${planLabels} ${partialIndexes.length > 1 ? 'are' : 'is'} ` +
        `missing some fields (description, timeline, or commitment). ` +
        `Please complete or remove ${partialIndexes.length > 1 ? 'them' : 'it'} before submitting.`
      );
      // Switch to the first partial plan so the user can see and fix it
      setActiveIndex(partialIndexes[0]);
      return;
    }

    const completePlans = actionPlans.filter(isComplete);
    if (completePlans.length === 0) return;

    // Clear the in-progress draft — submission is going through; the form
    // will swap to the results view.
    if (draftKey) {
      try { sessionStorage.removeItem(draftKey); } catch (_) {}
    }

    onSubmit({
      actionPlans: completePlans.map((ap) => ({
        text: ap.text,
        byWhen: ap.byWhen,
        commitment: ap.commitment,
      })),
      // Backward-compat fields
      text: completePlans[0].text,
      byWhen: completePlans[0].byWhen,
      commitment: completePlans[0].commitment,
      timestamp: new Date().toISOString(),
    });
  };

  // Activity Ended but Not Responded View
  if (isActivityEnded && !showResults) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBackToList && (
                  <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div className="flex items-center">
                  <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                  <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-neutral-100 border border-neutral-300 rounded-full">
                  <div className="w-1.5 h-1.5 bg-neutral-500 rounded-full"></div>
                  <span className="text-xs font-medium text-neutral-600">Ended</span>
                </div>
                {sessionCode && (
                  <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                    <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Type Heading */}
        <div className="bg-primary-50 px-6 py-3">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-lg font-bold text-primary-600 tracking-wide">ACTION PLAN</h2>
          </div>
        </div>

        {/* Not Attended Message */}
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="bg-neutral-100 border border-neutral-300 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-neutral-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-neutral-700 mb-2">Activity Ended</h2>
            <p className="text-neutral-600 mb-4">You didn't attend this action plan activity.</p>
            <p className="text-sm text-neutral-500">This activity has already ended, so you can no longer submit a response.</p>
            {onBackToList && (
              <button
                onClick={onBackToList}
                className="mt-6 px-6 py-2 bg-neutral-600 hover:bg-neutral-700 text-white font-semibold rounded-lg transition-colors"
              >
                Back to Activities
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Results View
  if (showResults) {
    // Get all submitted action plans
    const displayPlans = userActionPlan?.actionPlans ||
      (userActionPlan?.text ? [{
        text: userActionPlan.text,
        byWhen: userActionPlan.byWhen,
        commitment: userActionPlan.commitment,
      }] : actionPlans.filter(ap => ap.text.trim()));

    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBackToList && (
                  <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div className="flex items-center">
                  <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                  <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isActivityEnded ? (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-neutral-100 border border-neutral-300 rounded-full">
                    <div className="w-1.5 h-1.5 bg-neutral-500 rounded-full"></div>
                    <span className="text-xs font-medium text-neutral-600">Ended</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-medium text-green-700">Live</span>
                  </div>
                )}
                {sessionCode && (
                  <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                    <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Type Heading */}
        <div className="bg-primary-50 px-6 py-3">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-lg font-bold text-primary-600 tracking-wide">ACTION PLAN</h2>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Success Message */}
          <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-green-800">
                  {displayPlans.length > 1 ? `${displayPlans.length} Action Plans Submitted!` : 'Action Plan Submitted!'}
                </h2>
                <p className="text-sm sm:text-base text-green-700">
                  {isActivityEnded
                    ? 'This activity has ended. Your action plans have been recorded.'
                    : 'Great job planning your next steps'}
                </p>
              </div>
            </div>
          </div>

          {/* Action Plan Cards */}
          <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6 mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-neutral-900 mb-4">{activity.title}</h3>

            <div className="space-y-6">
              {displayPlans.map((plan, idx) => (
                <div key={idx} className={idx > 0 ? 'pt-6 border-t border-neutral-200' : ''}>
                  {displayPlans.length > 1 && (
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                        {idx + 1}
                      </span>
                      <span className="text-sm font-medium text-neutral-600">Action Plan {idx + 1}</span>
                    </div>
                  )}

                  {/* Action Plan Text */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-neutral-700">Your Action Plan</h4>
                      <span className="text-xs text-neutral-500">
                        {(plan.text || '').length} characters
                      </span>
                    </div>
                    <div className="p-3 sm:p-4 bg-primary-50 border border-primary-200 rounded-xl">
                      <p className="text-sm sm:text-base text-neutral-900 leading-relaxed whitespace-pre-wrap">
                        {plan.text || 'No response recorded'}
                      </p>
                    </div>
                  </div>

                  {/* Timeline & Commitment Row */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* By When */}
                    <div className="p-3 bg-blue-50 rounded-xl">
                      <p className="text-xs text-blue-600 font-medium mb-1">Target Timeline</p>
                      <p className="text-sm font-semibold text-blue-900">
                        {plan.byWhen ? getByWhenLabel(plan.byWhen) : 'Not specified'}
                      </p>
                    </div>

                    {/* Commitment Level */}
                    <div className="p-3 bg-purple-50 rounded-xl">
                      <p className="text-xs text-purple-600 font-medium mb-1">Commitment Level</p>
                      <p className="text-sm font-semibold text-purple-900">
                        {plan.commitment ? `${plan.commitment}/10 (${getCommitmentDescription(plan.commitment)})` : 'Not specified'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {userActionPlan?.timestamp && (
                <p className="text-xs text-neutral-500 pt-4 border-t border-neutral-100">
                  Submitted on {formatActualDateTime(userActionPlan.timestamp)}
                </p>
              )}
            </div>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-green-50 rounded-xl border border-green-100">
            <div className="flex gap-3">
              <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-green-800">Keep track of your action plans</p>
                <p className="text-sm text-green-700">Your trainer will provide feedback and support to help you achieve your goals.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Input View - With By When and Commitment
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {onBackToList && (
                <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <div className="flex items-center">
                <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-medium text-green-700">Live</span>
              </div>
              {sessionCode && (
                <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                  <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Activity Type Heading */}
      <div className="bg-primary-50 px-6 py-3">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-lg font-bold text-primary-600 tracking-wide">ACTION PLAN</h2>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Left Side - Action Plan Input */}
          <div className="flex-1 order-2 lg:order-1">
            {/* Title */}
            <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-3 sm:mb-4 leading-tight">
              {activity.title || 'Create Your Action Plan'}
            </h1>

            <p className="text-base sm:text-lg text-neutral-600 mb-6 sm:mb-8">
              {activity.question || 'What specific action will you take to apply what you learned?'}
            </p>

            {/* Action Plan Tabs - Show when multiple plans exist */}
            {actionPlans.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {actionPlans.map((plan, idx) => {
                  const isPlanComplete = plan.text.trim() && plan.byWhen && plan.commitment > 0;
                  return (
                    <button
                      key={idx}
                      onClick={() => setActiveIndex(idx)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
                        activeIndex === idx
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300'
                      }`}
                    >
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                        isPlanComplete ? 'bg-green-500 text-white' : activeIndex === idx ? 'bg-primary-500 text-white' : 'bg-neutral-200 text-neutral-600'
                      }`}>
                        {isPlanComplete ? (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          idx + 1
                        )}
                      </span>
                      <span className="text-sm font-medium">Action {idx + 1}</span>
                      {actionPlans.length > 1 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeActionPlan(idx);
                          }}
                          className="ml-1 text-neutral-400 hover:text-red-500 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Step 1: Action Plan Text */}
            <div className="bg-neutral-50 rounded-xl p-4 sm:p-6 mb-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-900">Describe Your Action Plan</h3>
                  <p className="text-sm text-neutral-500">Be specific about what you will do</p>
                </div>
              </div>

              <textarea
                value={currentPlan.text}
                onChange={(e) => updateCurrentPlan('text', e.target.value)}
                placeholder="I will..."
                rows={5}
                maxLength={1000}
                className="w-full px-4 py-3 text-neutral-900 bg-white border-2 border-neutral-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 resize-none transition-all"
              />
              <p className="text-xs text-neutral-500 mt-2">{currentPlan.text.length}/1000 characters</p>
            </div>

            {/* Step 2: By When */}
            <div className="bg-neutral-50 rounded-xl p-4 sm:p-6 mb-4">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  currentPlan.text.trim() ? 'bg-blue-500 text-white' : 'bg-neutral-300 text-neutral-500'
                }`}>
                  2
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-900">By when</h3>
                  <p className="text-sm text-neutral-500">Set a target timeline for your action</p>
                </div>
              </div>

              <select
                value={currentPlan.byWhen}
                onChange={(e) => updateCurrentPlan('byWhen', e.target.value)}
                className={`w-full px-4 py-3 text-neutral-900 bg-white border-2 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all appearance-none cursor-pointer ${
                  currentPlan.byWhen ? 'border-blue-500' : 'border-neutral-200'
                }`}
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '2.5rem',
                }}
              >
                <option value="">Select timeline...</option>
                {BY_WHEN_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Step 3: Commitment Level */}
            <div className="bg-neutral-50 rounded-xl p-4 sm:p-6 mb-4">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  currentPlan.byWhen ? 'bg-blue-500 text-white' : 'bg-neutral-300 text-neutral-500'
                }`}>
                  3
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-900">How committed are you to implement this on a scale of 1-10</h3>
                  <p className="text-sm text-neutral-500">Rate your commitment level</p>
                </div>
              </div>

              <select
                value={currentPlan.commitment || ''}
                onChange={(e) => updateCurrentPlan('commitment', parseInt(e.target.value, 10))}
                className={`w-full px-4 py-3 text-neutral-900 bg-white border-2 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all appearance-none cursor-pointer ${
                  currentPlan.commitment > 0 ? 'border-purple-500' : 'border-neutral-200'
                }`}
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '2.5rem',
                }}
              >
                <option value="">Select commitment level...</option>
                {COMMITMENT_LEVELS.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>

              {currentPlan.commitment > 0 && (
                <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-800">
                    <span className="font-semibold">Your commitment: {currentPlan.commitment}/10</span>
                    <span className="text-purple-600"> - {getCommitmentDescription(currentPlan.commitment)}</span>
                  </p>
                </div>
              )}
            </div>

            {/* + Add another action button */}
            {actionPlans.length < MAX_ACTION_PLANS && (
              <button
                onClick={addActionPlan}
                className="w-full mb-4 py-3 px-4 border-2 border-dashed border-neutral-300 rounded-xl text-neutral-600 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50 transition-all flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span className="font-medium">+ Add another action</span>
              </button>
            )}

            {/* Submit Button */}
            <div className="mb-8">
              {submitError && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-sm text-red-700">{submitError}</p>
                </div>
              )}
              <button
                onClick={handleSubmit}
                disabled={!hasCompletePlan}
                className="w-full sm:w-auto px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-neutral-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors"
              >
                {hasCompletePlan
                  ? `Submit ${actionPlans.filter(ap => ap.text.trim() && ap.byWhen && ap.commitment > 0).length} Action Plan${actionPlans.filter(ap => ap.text.trim() && ap.byWhen && ap.commitment > 0).length > 1 ? 's' : ''}`
                  : 'Complete all steps to submit'}
              </button>

              {!isCurrentPlanComplete && (
                <p className="mt-2 text-sm text-neutral-500">
                  {!currentPlan.text.trim() && 'Step 1: Describe your action plan'}
                  {currentPlan.text.trim() && !currentPlan.byWhen && 'Step 2: Select a timeline'}
                  {currentPlan.text.trim() && currentPlan.byWhen && !currentPlan.commitment && 'Step 3: Rate your commitment'}
                </p>
              )}

              {actionPlans.length > 1 && (
                <p className="mt-2 text-xs text-neutral-400">
                  {actionPlans.filter(ap => ap.text.trim() && ap.byWhen && ap.commitment > 0).length} of {actionPlans.length} action plans complete
                </p>
              )}
            </div>
          </div>

          {/* Right Side - Timer & Info */}
          <div className="w-full lg:w-64 flex-shrink-0 order-1 lg:order-2">
            <div className="lg:sticky lg:top-8">
              {/* Circular Timer */}
              {timerEnabled && (
              <div className="flex flex-row lg:flex-col items-center gap-4 lg:gap-0 mb-6 lg:mb-8 p-4 lg:p-0 bg-neutral-50 lg:bg-transparent rounded-xl lg:rounded-none">
                <div className="relative w-20 h-20 lg:w-24 lg:h-24">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 96 96">
                    <circle cx="48" cy="48" r="40" stroke="#e5e7eb" strokeWidth="6" fill="none" />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="#ff8a00"
                      strokeWidth="6"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 40}`}
                      strokeDashoffset={`${2 * Math.PI * 40 * (1 - timeRemaining / maxTime)}`}
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg lg:text-2xl font-bold text-primary-600">{formatTime(timeRemaining)}</span>
                  </div>
                </div>
                <span className="text-sm text-neutral-500 lg:mt-2">Time Remaining</span>
              </div>
              )}

              {/* Progress Checklist - Desktop */}
              <div className="hidden lg:block bg-primary-50 rounded-xl p-4 border border-primary-100">
                <h4 className="font-semibold text-primary-900 mb-3">
                  {actionPlans.length > 1 ? `Action ${activeIndex + 1} Progress` : 'Your Progress'}
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                      currentPlan.text.trim() ? 'bg-green-500' : 'bg-neutral-200'
                    }`}>
                      {currentPlan.text.trim() && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className={`text-sm ${currentPlan.text.trim() ? 'text-green-700' : 'text-neutral-600'}`}>
                      Action plan
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                      currentPlan.byWhen ? 'bg-green-500' : 'bg-neutral-200'
                    }`}>
                      {currentPlan.byWhen && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className={`text-sm ${currentPlan.byWhen ? 'text-green-700' : 'text-neutral-600'}`}>
                      Timeline set
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                      currentPlan.commitment > 0 ? 'bg-green-500' : 'bg-neutral-200'
                    }`}>
                      {currentPlan.commitment > 0 && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className={`text-sm ${currentPlan.commitment > 0 ? 'text-green-700' : 'text-neutral-600'}`}>
                      Commitment rated
                    </span>
                  </div>
                </div>
                {actionPlans.length > 1 && (
                  <div className="mt-3 pt-3 border-t border-primary-200">
                    <p className="text-xs text-primary-700">
                      {actionPlans.filter(ap => ap.text.trim() && ap.byWhen && ap.commitment > 0).length} of {actionPlans.length} plans complete
                    </p>
                  </div>
                )}
              </div>

              {/* Why This Matters - Desktop */}
              <div className="hidden lg:block mt-4 bg-blue-50 rounded-xl p-4 border border-blue-100">
                <h4 className="font-semibold text-blue-900 mb-2 text-sm">Why This Matters</h4>
                <p className="text-xs text-blue-700">
                  Setting a clear timeline and commitment level helps you stay accountable and increases your chances of taking action.
                </p>
              </div>

              {/* Privacy Notice */}
              <div className="hidden lg:flex mt-4 items-center gap-2 text-sm text-neutral-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>Shared with trainer only</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionPlansActivity;
